import React from "react";
import { Award, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";
import './css/ResultDetails.css';

const ResultDetails = ({ setSideBar, setSettingDrawer }) => {
  const { examId } = useParams();
  const navigate = useNavigate();

  // Placeholder student and result data; replace with backend API
  const student = {
    name: "John Doe",
    fatherName: "Michael Doe",
    rollNo: "A12345",
    mobile: "+1 234 567 8900",
    class: "10th Grade",
  };

  const resultData = {
    1: {
      examName: "Mid Term",
      totalMarks: 500,
      obtainedMarks: 425,
      percentage: 85,
      status: "Pass",
      date: "2025-06-15",
      subjects: [
        { name: "Mathematics", totalMarks: 100, obtainedMarks: 90, percentage: 90 },
        { name: "Science", totalMarks: 100, obtainedMarks: 85, percentage: 85 },
        { name: "English", totalMarks: 100, obtainedMarks: 80, percentage: 80 },
        { name: "History", totalMarks: 100, obtainedMarks: 88, percentage: 88 },
        { name: "Geography", totalMarks: 100, obtainedMarks: 82, percentage: 82 },
      ],
    },
    2: {
      examName: "Final Exam",
      totalMarks: 600,
      obtainedMarks: 360,
      percentage: 60,
      status: "Pass",
      date: "2025-12-10",
      subjects: [
        { name: "Mathematics", totalMarks: 120, obtainedMarks: 72, percentage: 60 },
        { name: "Science", totalMarks: 120, obtainedMarks: 70, percentage: 58 },
        { name: "English", totalMarks: 120, obtainedMarks: 75, percentage: 62 },
        { name: "History", totalMarks: 120, obtainedMarks: 68, percentage: 57 },
        { name: "Geography", totalMarks: 120, obtainedMarks: 75, percentage: 62 },
      ],
    },
    3: {
      examName: "Quarterly Exam",
      totalMarks: 400,
      obtainedMarks: 280,
      percentage: 70,
      status: "Pass",
      date: "2025-03-20",
      subjects: [
        { name: "Mathematics", totalMarks: 80, obtainedMarks: 60, percentage: 75 },
        { name: "Science", totalMarks: 80, obtainedMarks: 55, percentage: 69 },
        { name: "English", totalMarks: 80, obtainedMarks: 58, percentage: 72 },
        { name: "History", totalMarks: 80, obtainedMarks: 57, percentage: 71 },
      ],
    },
    4: {
      examName: "Half Yearly",
      totalMarks: 500,
      obtainedMarks: 200,
      percentage: 40,
      status: "Fail",
      date: "2025-09-25",
      subjects: [
        { name: "Mathematics", totalMarks: 100, obtainedMarks: 45, percentage: 45 },
        { name: "Science", totalMarks: 100, obtainedMarks: 40, percentage: 40 },
        { name: "English", totalMarks: 100, obtainedMarks: 38, percentage: 38 },
        { name: "History", totalMarks: 100, obtainedMarks: 42, percentage: 42 },
        { name: "Geography", totalMarks: 100, obtainedMarks: 35, percentage: 35 },
      ],
    },
  };

  const result = resultData[examId] || {
    examName: "Unknown Exam",
    totalMarks: 0,
    obtainedMarks: 0,
    percentage: 0,
    status: "Unknown",
    date: "N/A",
    subjects: [],
  };

  return (
    <>
      <TopBar title="Result Details" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
      <div className="result-details-container">
        <button
          className="back-button"
          onClick={() => navigate("/results")}
          aria-label="Back to Results"
        >
          <ArrowLeft size={20} /> Back
        </button>
        <div className="result-card">
          <div className="result-header">
            <Award size={28} className="header-icon" />
            <h2>Result Details</h2>
          </div>
          <div className="result-content">
            <div className="student-details">
              <h3>Student Details</h3>
              <div className="detail-row">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{student.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Father's Name:</span>
                <span className="detail-value">{student.fatherName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Roll No:</span>
                <span className="detail-value">{student.rollNo}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Mobile:</span>
                <span className="detail-value">{student.mobile}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Class:</span>
                <span className="detail-value">{student.class}</span>
              </div>
            </div>
            <div className="exam-details">
              <h3>Exam Details</h3>
              <div className="detail-row">
                <span className="detail-label">Exam Name:</span>
                <span className="detail-value">{result.examName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date:</span>
                <span className="detail-value">{result.date}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Marks:</span>
                <span className="detail-value">{result.obtainedMarks}/{result.totalMarks}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Percentage:</span>
                <span className="detail-value">{result.percentage}%</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status ${result.status.toLowerCase()}`}>
                  {result.status === "Pass" ? (
                    <CheckCircle size={16} />
                  ) : (
                    <XCircle size={16} />
                  )}
                  {result.status}
                </span>
              </div>
            </div>
            <div className="subject-details">
              <h3>Subject-wise Marks</h3>
              <div className="subject-table">
                <div className="subject-table-header">
                  <span>Subject</span>
                  <span>Marks Obtained</span>
                  <span>Total Marks</span>
                  <span>Percentage</span>
                </div>
                {result.subjects.map((subject, index) => (
                  <div key={index} className="subject-table-row">
                    <span>{subject.name}</span>
                    <span>{subject.obtainedMarks}</span>
                    <span>{subject.totalMarks}</span>
                    <span>{subject.percentage}%</span>
                  </div>
                ))}
                {result.subjects.length === 0 && (
                  <div className="no-subjects">No subject details available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultDetails;